import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { AuthContext } from '../../../components/AuthContext';
import Swal from 'sweetalert2';
import Select from 'react-select';
import { 
  FaCalendarAlt, FaMapMarkerAlt, FaFileExport, FaSearch, FaDownload,
  FaArrowRight, FaArrowLeft, FaBox, FaWarehouse, FaTruck, FaTrashAlt,
  FaCube, FaLayerGroup, FaExclamationTriangle, FaExpand, FaCompress
} from 'react-icons/fa';

export default function TilesLedger() {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [ledgerData, setLedgerData] = useState(null);
  const [locations, setLocations] = useState([]);
  const [tilesList, setTilesList] = useState([]);
  const [filters, setFilters] = useState({
    dateFrom: '', dateTo: '', location: null, tileId: null
  });
  const [locationOptions, setLocationOptions] = useState([]);
  const [tileOptions, setTileOptions] = useState([]);

  // Dark mode
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');
  useEffect(() => {
    const sync = () => setDarkMode(localStorage.getItem('darkMode') === 'true');
    const interval = setInterval(sync, 500);
    return () => clearInterval(interval);
  }, []);

  // Fullscreen
  const containerRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  }, []);
  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  // React-select dark styles
  const getSelectStyles = (isDark) => ({
    control: (base, state) => ({
      ...base,
      backgroundColor: isDark ? '#374151' : 'white',
      borderColor: state.isFocused ? '#7c3aed' : isDark ? '#4b5563' : '#d1d5db',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(124,58,237,0.2)' : 'none',
      borderRadius: '8px', minHeight: '30px', height: '30px', fontSize: '0.875rem',
      '&:hover': { borderColor: '#7c3aed' }
    }),
    valueContainer: (base) => ({ ...base, height: '30px', padding: '0 6px' }),
    indicatorsContainer: (base) => ({ ...base, height: '30px' }),
    menu: (base) => ({ ...base, backgroundColor: isDark ? '#374151' : 'white', border: isDark ? '1px solid #4b5563' : '1px solid #d1d5db', zIndex: 50 }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? '#7c3aed' : state.isFocused ? (isDark ? '#4b5563' : '#f3f4f6') : 'transparent',
      color: state.isSelected ? 'white' : isDark ? '#e5e7eb' : '#374151'
    }),
    singleValue: (base) => ({ ...base, color: isDark ? '#e5e7eb' : '#374151' }),
    input: (base) => ({ ...base, color: isDark ? '#e5e7eb' : '#374151' }),
    placeholder: (base) => ({ ...base, color: '#9ca3af' })
  });

  const getCompanyId = () => user?.user?.company || '';

  useEffect(() => {
    fetchLocations();
    fetchTilesList();
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 2);
    setFilters({ dateFrom: startOfMonth.toISOString().split('T')[0], dateTo: now.toISOString().split('T')[0], location: null, tileId: null });
  }, []);

  const fetchLocations = async () => {
    try {
      const companyId = getCompanyId();
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/locations${companyId ? `/companyId/${companyId}` : ''}`, { credentials: 'include' });
      const data = await response.json();
      const locs = Array.isArray(data) ? data : [];
      setLocations(locs);
      setLocationOptions(locs.map(l => ({ value: l._id, label: l.name || l.locationName, location: l })));
    } catch (error) {
      console.error('Error fetching locations:', error);
      Swal.fire('Error', 'Failed to fetch locations', 'error');
    }
  };

  const fetchTilesList = async () => {
    try {
      const companyId = getCompanyId();
      const params = new URLSearchParams();
      if (companyId) params.append('companyId', companyId);
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/reports/tiles/tiles-list?${params}`, { credentials: 'include' });
      const data = await response.json();
      if (data.success) {
        const tiles = data.data || [];
        setTilesList(tiles);
        setTileOptions(tiles.map(t => ({ value: t._id, label: `${t.tilesName} (${t.id})`, tile: t })));
      }
    } catch (error) {
      console.error('Error fetching tiles list:', error);
      Swal.fire('Error', 'Failed to fetch tiles list', 'error');
    }
  };

  const fetchLedgerData = async () => {
    if (!filters.dateFrom || !filters.dateTo) { Swal.fire('Error', 'Please select date range', 'error'); return; }
    if (!filters.location) { Swal.fire({ icon: 'error', title: 'Location Required', text: 'Please select a location to generate the ledger report' }); return; }
    if (!filters.tileId) { Swal.fire('Error', 'Please select a tile', 'error'); return; }

    setLoading(true);
    try {
      const companyId = getCompanyId();
      const params = new URLSearchParams({
        dateFrom: filters.dateFrom, dateTo: filters.dateTo,
        tileId: filters.tileId.value, location: filters.location.value,
        ...(companyId && { companyId })
      });
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/reports/tiles/ledger?${params}`, { credentials: 'include' });
      const data = await response.json();
      if (data.success) { setLedgerData(data.data); } else { throw new Error(data.message || 'Failed to fetch data'); }
    } catch (error) {
      console.error('Error fetching ledger data:', error);
      Swal.fire('Error', 'Failed to fetch ledger data', 'error');
      setLedgerData(null);
    } finally { setLoading(false); }
  };

  const handleFilterChange = (field, value) => setFilters(prev => ({ ...prev, [field]: value }));

  const exportToCSV = async () => {
    if (!filters.dateFrom || !filters.dateTo || !filters.tileId || !filters.location) { Swal.fire('Error', 'Please select tile, location and date range', 'error'); return; }
    setLoading(true);
    try {
      const companyId = getCompanyId();
      const params = new URLSearchParams({ dateFrom: filters.dateFrom, dateTo: filters.dateTo, tileId: filters.tileId.value, location: filters.location.value, ...(companyId && { companyId }), format: 'csv' });
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/reports/tiles/ledger?${params}`, { credentials: 'include' });
      if (response.ok) {
        const blob = await response.blob(); const url = window.URL.createObjectURL(blob); const link = document.createElement('a');
        link.href = url; link.setAttribute('download', `Tiles_Ledger_${filters.tileId.label}_${filters.dateFrom}_to_${filters.dateTo}.csv`);
        document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(url);
        Swal.fire('Success', 'CSV file downloaded successfully', 'success');
      } else { throw new Error('Failed to download CSV file'); }
    } catch (error) { console.error('Error downloading CSV:', error); Swal.fire('Error', 'Failed to download CSV file', 'error'); }
    finally { setLoading(false); }
  };

  const exportToExcel = async () => {
    if (!filters.dateFrom || !filters.dateTo || !filters.tileId || !filters.location) { Swal.fire('Error', 'Please select tile, location and date range', 'error'); return; }
    setLoading(true);
    try {
      const companyId = getCompanyId();
      const params = new URLSearchParams({ dateFrom: filters.dateFrom, dateTo: filters.dateTo, tileId: filters.tileId.value, location: filters.location.value, ...(companyId && { companyId }), format: 'excel' });
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/reports/tiles/ledger?${params}`, { credentials: 'include' });
      if (response.ok) {
        const blob = await response.blob(); const url = window.URL.createObjectURL(blob); const link = document.createElement('a');
        link.href = url; link.setAttribute('download', `Tiles_Ledger_${filters.tileId.label}_${filters.dateFrom}_to_${filters.dateTo}.xlsx`);
        document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(url);
        Swal.fire('Success', 'Excel file downloaded successfully', 'success');
      } else { throw new Error('Failed to download Excel file'); }
    } catch (error) { console.error('Error downloading Excel:', error); Swal.fire('Error', 'Failed to download Excel file', 'error'); }
    finally { setLoading(false); }
  };

  const formatDate = (dateString) => new Date(dateString).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });

  const getTransactionTypeIcon = (type) => {
    switch (type) {
      case 'INWARD': return <FaArrowRight className={darkMode ? 'text-green-400' : 'text-green-600'} />;
      case 'OUTWARD': return <FaArrowLeft className={darkMode ? 'text-red-400' : 'text-red-600'} />;
      case 'DEMOLISHED': return <FaTrashAlt className={darkMode ? 'text-orange-400' : 'text-orange-600'} />;
      case 'TRANSFER_IN': return <FaTruck className={darkMode ? 'text-blue-400' : 'text-blue-600'} />;
      case 'TRANSFER_OUT': return <FaTruck className={darkMode ? 'text-orange-400' : 'text-orange-600'} />;
      default: return <FaBox className={darkMode ? 'text-gray-400' : 'text-gray-600'} />;
    }
  };

  const getTransactionTypeColor = (type) => {
    if (darkMode) {
      switch (type) {
        case 'INWARD': return 'bg-green-900/40 text-green-300 border-green-700';
        case 'OUTWARD': return 'bg-red-900/40 text-red-300 border-red-700';
        case 'DEMOLISHED': return 'bg-orange-900/40 text-orange-300 border-orange-700';
        case 'TRANSFER_IN': return 'bg-blue-900/40 text-blue-300 border-blue-700';
        case 'TRANSFER_OUT': return 'bg-orange-900/40 text-orange-300 border-orange-700';
        default: return 'bg-gray-700 text-gray-300 border-gray-600';
      }
    }
    switch (type) {
      case 'INWARD': return 'bg-green-50 text-green-700 border-green-200';
      case 'OUTWARD': return 'bg-red-50 text-red-700 border-red-200';
      case 'DEMOLISHED': return 'bg-orange-50 text-orange-700 border-orange-200';
      case 'TRANSFER_IN': return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'TRANSFER_OUT': return 'bg-orange-50 text-orange-700 border-orange-200';
      default: return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  // Reusable class helpers
  const cardCls = darkMode ? 'bg-gray-800/80 backdrop-blur-sm border-gray-700' : 'bg-white border-gray-200';
  const textP = darkMode ? 'text-white' : 'text-gray-900';
  const textS = darkMode ? 'text-gray-400' : 'text-gray-600';
  const textM = darkMode ? 'text-gray-500' : 'text-gray-400';
  const borderCls = darkMode ? 'border-gray-700' : 'border-gray-200';
  const inputCls = darkMode
    ? 'bg-gray-700 border-gray-600 text-white focus:ring-purple-500'
    : 'border-gray-300 focus:ring-purple-500';
  const filterBg = darkMode ? 'bg-gray-700/50' : 'bg-gray-50';
  const inwardBg = darkMode ? 'bg-green-900/30' : 'bg-green-50';
  const inwardText = darkMode ? 'text-green-300' : 'text-green-700';
  const outwardBg = darkMode ? 'bg-red-900/30' : 'bg-red-50';
  const outwardText = darkMode ? 'text-red-300' : 'text-red-700';

  return (
    <div ref={containerRef} className={`min-h-screen p-4 transition-colors duration-300 ${darkMode ? 'bg-gray-900' : 'bg-gray-50'} ${isFullscreen ? 'overflow-auto' : ''}`}>
      <div className="w-full mx-auto">
        {/* Header & Filters */}
        <div className={`rounded-xl shadow-sm border mb-4 transition-colors duration-300 ${cardCls}`}>
          <div className={`px-4 py-3 border-b ${borderCls}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="bg-gradient-to-br from-purple-500 to-indigo-600 text-white p-2 rounded-lg">
                  <FaLayerGroup className="w-4 h-4" />
                </div>
                <h1 className={`text-xl font-bold ${textP}`}>Tiles Ledger</h1>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-sm ${textS}`}>
                  {user?.user?.role === 'CompanyHead' ? user?.user?.companyName || 'Company User' : 'All Companies'}
                </span>
                <button onClick={toggleFullscreen} className={`p-2 rounded-lg transition-colors ${darkMode ? 'hover:bg-gray-700 text-gray-400 hover:text-white' : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'}`} title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}>
                  {isFullscreen ? <FaCompress /> : <FaExpand />}
                </button>
              </div>
            </div>
          </div>

          {/* Compact Filters */}
          <div className={`p-3 ${filterBg} transition-colors duration-300`}>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
              <div>
                <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <FaCalendarAlt className="inline mr-1" size={10} /> Date From *
                </label>
                <input type="date" value={filters.dateFrom} onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                  className={`w-full px-2 py-1 text-sm border rounded-md focus:ring-1 focus:border-transparent transition-colors ${inputCls}`} />
              </div>
              <div>
                <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <FaCalendarAlt className="inline mr-1" size={10} /> Date To *
                </label>
                <input type="date" value={filters.dateTo} onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                  className={`w-full px-2 py-1 text-sm border rounded-md focus:ring-1 focus:border-transparent transition-colors ${inputCls}`} />
              </div>
              <div>
                <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <FaCube className="inline mr-1" size={10} /> Tile *
                </label>
                <Select value={filters.tileId} onChange={(v) => handleFilterChange('tileId', v)} options={tileOptions}
                  placeholder="Select Tile" isClearable isSearchable styles={getSelectStyles(darkMode)} />
              </div>
              <div>
                <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <FaMapMarkerAlt className="inline mr-1" size={10} /> Location *
                </label>
                <Select value={filters.location} onChange={(v) => handleFilterChange('location', v)} options={locationOptions}
                  placeholder="Select Location" isClearable isSearchable styles={getSelectStyles(darkMode)} />
              </div>
              <div className="flex items-center gap-1 md:col-span-2 mt-3 md:mt-0">
                <button onClick={fetchLedgerData} disabled={loading}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-3 py-1 text-sm rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all disabled:opacity-50 flex items-center justify-center gap-1 shadow-sm">
                  {loading ? (<><div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" /><span>Loading</span></>) : (<><FaSearch size={12} /><span>Generate</span></>)}
                </button>
                <button onClick={exportToCSV} disabled={!ledgerData}
                  className="bg-emerald-600 text-white px-2 py-1 text-sm rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-40 flex items-center gap-1 shadow-sm" title="Export as CSV">
                  <FaDownload size={12} /><span>CSV</span>
                </button>
                <button onClick={exportToExcel} disabled={!ledgerData || loading}
                  className="bg-blue-600 text-white px-2 py-1 text-sm rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-40 flex items-center gap-1 shadow-sm" title="Export as Excel">
                  <FaFileExport size={12} /><span>Excel</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Results */}
        {ledgerData ? (
          <div className="space-y-4">
            {/* Tile Info Header + Stock Cards */}
            <div className={`rounded-xl shadow-sm border p-3 transition-colors duration-300 ${cardCls}`}>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-3 gap-2">
                <div>
                  <h3 className={`font-bold ${textP}`}>
                    {ledgerData.tile.tilesName} <span className={`text-sm ${textS}`}>({ledgerData.tile.id})</span>
                  </h3>
                  <div className={`text-sm ${textS}`}>{ledgerData.tile.category} | {ledgerData.tile.sizeMM} MM | {ledgerData.tile.brand}</div>
                </div>
                <div className={`text-sm ${textS}`}>Period: {formatDate(filters.dateFrom)} to {formatDate(filters.dateTo)}</div>
              </div>

              {/* Stock Cards */}
              <div className="grid grid-cols-3 gap-3 text-xs">
                {/* Opening Stock */}
                <div className={`rounded-lg p-3 border transition-colors ${darkMode ? 'bg-green-900/20 border-green-800/50' : 'bg-green-50 border-green-200'}`}>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className={`font-semibold flex items-center gap-1 ${darkMode ? 'text-green-300' : 'text-green-900'}`}>
                      <FaBox size={10} /> Opening Stock
                    </h4>
                    <span className={`text-xs ${darkMode ? 'text-green-400' : 'text-green-700'}`}>{formatDate(filters.dateFrom)}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-1">
                    {['fsBox','fsPieces','dsBox','dsPieces'].map(k => (
                      <div key={k}>
                        <div className={`font-medium ${darkMode ? 'text-green-400' : 'text-green-700'}`}>{k.replace('fs','FS ').replace('ds','DS ').replace('Box','Box').replace('Pieces','Pcs')}</div>
                        <div className={darkMode ? 'text-green-200' : 'text-green-900'}>{ledgerData.openingStock?.[k] || 0}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Current Stock */}
                <div className={`rounded-lg p-3 border transition-colors ${darkMode ? 'bg-purple-900/20 border-purple-800/50' : 'bg-purple-50 border-purple-200'}`}>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className={`font-semibold flex items-center gap-1 ${darkMode ? 'text-purple-300' : 'text-purple-900'}`}>
                      <FaWarehouse size={10} /> Current Stock
                    </h4>
                    <span className={`text-xs ${darkMode ? 'text-purple-400' : 'text-purple-700'}`}>System Live</span>
                  </div>
                  <div className="grid grid-cols-2 gap-1">
                    {['fsBox','fsPieces','dsBox','dsPieces'].map(k => (
                      <div key={k}>
                        <div className={`font-medium ${darkMode ? 'text-purple-400' : 'text-purple-700'}`}>{k.replace('fs','FS ').replace('ds','DS ').replace('Box','Box').replace('Pieces','Pcs')}</div>
                        <div className={darkMode ? 'text-purple-200' : 'text-purple-900'}>{ledgerData.currentStock?.[k] || 0}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Closing Stock */}
                <div className={`rounded-lg p-3 border transition-colors ${darkMode ? 'bg-blue-900/20 border-blue-800/50' : 'bg-blue-50 border-blue-200'}`}>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className={`font-semibold flex items-center gap-1 ${darkMode ? 'text-blue-300' : 'text-blue-900'}`}>
                      <FaLayerGroup size={10} /> Closing Stock
                    </h4>
                    <span className={`text-xs ${darkMode ? 'text-blue-400' : 'text-blue-700'}`}>{formatDate(filters.dateTo)}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-1">
                    {['fsBox','fsPieces','dsBox','dsPieces'].map(k => (
                      <div key={k}>
                        <div className={`font-medium ${darkMode ? 'text-blue-400' : 'text-blue-700'}`}>{k.replace('fs','FS ').replace('ds','DS ').replace('Box','Box').replace('Pieces','Pcs')}</div>
                        <div className={darkMode ? 'text-blue-200' : 'text-blue-900'}>{ledgerData.closingStock?.[k] || 0}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Transactions Table */}
            <div className={`rounded-xl shadow-sm border transition-colors duration-300 ${cardCls}`}>
              <div className={`px-3 py-2 border-b ${borderCls} flex justify-between items-center`}>
                <h3 className={`text-sm font-semibold ${textP}`}>Transaction Details ({ledgerData.transactions.length})</h3>
                <div className={`flex items-center gap-2 text-xs ${textS}`}>
                  <div className="flex items-center gap-1">
                    <span className={`p-1 rounded ${darkMode ? 'bg-green-900/40' : 'bg-green-100'}`}><FaArrowRight className={darkMode ? 'text-green-400' : 'text-green-600'} size={8} /></span>
                    <span>Inward</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className={`p-1 rounded ${darkMode ? 'bg-red-900/40' : 'bg-red-100'}`}><FaArrowLeft className={darkMode ? 'text-red-400' : 'text-red-600'} size={8} /></span>
                    <span>Outward</span>
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full text-xs">
                  <thead className={darkMode ? 'bg-gray-900/50' : 'bg-gray-50'}>
                    <tr>
                      <th className={`px-2 py-1 text-left font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Date/Type</th>
                      <th className={`px-2 py-1 text-left font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Supplier</th>
                      <th className={`px-2 py-1 text-left font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Remarks</th>
                      <th className={`px-2 py-1 text-center font-medium ${inwardBg}`} colSpan="4"><span className={inwardText}>Inward</span></th>
                      <th className={`px-2 py-1 text-center font-medium ${outwardBg}`} colSpan="4"><span className={outwardText}>Outward</span></th>
                    </tr>
                    <tr className={darkMode ? 'bg-gray-900/50' : 'bg-gray-50'}>
                      <th className="px-2 py-1" colSpan="3"></th>
                      {['FS Box','FS Pcs','DS Box','DS Pcs'].map(h => <th key={'i'+h} className={`px-2 py-1 text-center ${inwardBg} ${inwardText}`}>{h}</th>)}
                      {['FS Box','FS Pcs','DS Box','DS Pcs'].map(h => <th key={'o'+h} className={`px-2 py-1 text-center ${outwardBg} ${outwardText}`}>{h}</th>)}
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${darkMode ? 'divide-gray-700/50' : 'divide-gray-200'}`}>
                    {ledgerData.transactions.map((t, i) => (
                      <tr key={i} className={`transition-colors ${darkMode ? 'hover:bg-gray-700/30' : 'hover:bg-gray-50'}`}>
                        <td className="px-2 py-1">
                          <div className="flex items-center gap-1">
                            {getTransactionTypeIcon(t.type)}
                            <div>
                              <div className={`font-medium ${textP}`}>{formatDate(t.date)}</div>
                              <div className={`text-xs px-1 py-0.5 rounded border inline-block ${getTransactionTypeColor(t.type)}`}>{t.type.replace('_', ' ')}</div>
                            </div>
                          </div>
                        </td>
                        <td className={`px-2 py-1 ${textP}`}>{t.supplier || 'N/A'}</td>
                        <td className={`px-2 py-1 ${textP}`}><div className="max-w-xs truncate" title={t.remarks}>{t.remarks || 'N/A'}</div></td>
                        <td className={`px-2 py-1 text-right font-medium ${inwardBg} ${inwardText}`}>{t.inward?.fsBox || 0}</td>
                        <td className={`px-2 py-1 text-right font-medium ${inwardBg} ${inwardText}`}>{t.inward?.fsPieces || 0}</td>
                        <td className={`px-2 py-1 text-right font-medium ${inwardBg} ${inwardText}`}>{t.inward?.dsBox || 0}</td>
                        <td className={`px-2 py-1 text-right font-medium ${inwardBg} ${inwardText}`}>{t.inward?.dsPieces || 0}</td>
                        <td className={`px-2 py-1 text-right font-medium ${outwardBg} ${outwardText}`}>{t.outward?.fsBox || 0}</td>
                        <td className={`px-2 py-1 text-right font-medium ${outwardBg} ${outwardText}`}>{t.outward?.fsPieces || 0}</td>
                        <td className={`px-2 py-1 text-right font-medium ${outwardBg} ${outwardText}`}>{t.outward?.dsBox || 0}</td>
                        <td className={`px-2 py-1 text-right font-medium ${outwardBg} ${outwardText}`}>{t.outward?.dsPieces || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Order / Transit Summary */}
            {ledgerData?.orderSummary && (
              <div className={`mt-4 rounded-xl border p-4 ${cardCls}`}>
                <h3 className={`text-sm font-semibold mb-3 ${textP}`}>Order / Transit Summary</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className={`rounded-lg p-3 ${darkMode ? 'bg-purple-900/20 border border-purple-800' : 'bg-purple-50 border border-purple-200'}`}>
                    <div className={`text-xs font-medium mb-2 ${darkMode ? 'text-purple-400' : 'text-purple-600'}`}>Purchase Orders</div>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between"><span className={textS}>Ordered (Period)</span><span className={`font-medium ${textP}`}>{ledgerData.orderSummary.dateRange?.ordered || 0} boxes</span></div>
                      <div className="flex justify-between"><span className={textS}>Pending Orders</span><span className={`font-medium ${textP}`}>{ledgerData.orderSummary.current?.pendingOrders || 0} boxes</span></div>
                    </div>
                  </div>
                  <div className={`rounded-lg p-3 ${darkMode ? 'bg-yellow-900/20 border border-yellow-800' : 'bg-yellow-50 border border-yellow-200'}`}>
                    <div className={`text-xs font-medium mb-2 ${darkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>In Transit</div>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between"><span className={textS}>Dispatched (Period)</span><span className={`font-medium ${textP}`}>{ledgerData.orderSummary.dateRange?.dispatched || 0} boxes</span></div>
                      <div className="flex justify-between"><span className={textS}>Currently In Transit</span><span className={`font-medium ${textP}`}>{ledgerData.orderSummary.current?.inTransit || 0} boxes</span></div>
                    </div>
                  </div>
                  <div className={`rounded-lg p-3 ${darkMode ? 'bg-blue-900/20 border border-blue-800' : 'bg-blue-50 border border-blue-200'}`}>
                    <div className={`text-xs font-medium mb-2 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>Sales Orders</div>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between"><span className={textS}>Booked (Period)</span><span className={`font-medium ${textP}`}>{(ledgerData.orderSummary.dateRange?.salesOrder?.freshBoxes || 0) + (ledgerData.orderSummary.dateRange?.salesOrder?.damagedBoxes || 0)} boxes</span></div>
                      <div className="flex justify-between"><span className={textS}>Pending Fulfillment</span><span className={`font-medium ${textP}`}>{(ledgerData.orderSummary.current?.pendingSalesOrders?.freshBoxes || 0) + (ledgerData.orderSummary.current?.pendingSalesOrders?.damagedBoxes || 0)} boxes</span></div>
                    </div>
                  </div>
                </div>
              </div>
            )}

          </div>
        ) : !loading && (
          <div className={`rounded-xl shadow-sm border p-6 text-center transition-colors duration-300 ${cardCls}`}>
            <FaWarehouse className={`mx-auto h-10 w-10 mb-3 ${textM}`} />
            <h3 className={`text-base font-medium mb-1 ${textP}`}>No Data Found</h3>
            <p className={`text-sm ${textS}`}>
              {filters.dateFrom && filters.dateTo && filters.tileId
                ? 'No transactions found for the selected criteria.'
                : 'Please select date range and tile, then click Generate to view the ledger report.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
